import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useEffect, useState } from 'react';
import BtnGradient from 'src/components/common/button/BtnGradient';
import BtnOutlineGradient from 'src/components/common/button/BtnOutlineGradient';
import { getAPI, postAPI } from 'src/utils/api';
import GenerateField from './GenerateField';
import { useNotify } from 'src/context/ContextNotify';
import dayjs from 'dayjs';
import { isEmpty, sortObject } from 'src/common/helper';
import { getToken } from 'src/utils/auth';

type DialogFormType = {
  open: boolean;
  handleClose: () => void;
  submission_id: string;
  handleConfirm: () => void;
};

const DialogForm: React.FC<DialogFormType> = (props) => {
  const notifyContext = useNotify();
  const [answerDetail, setAnswerDetail] = useState<any>(null);

  useEffect(() => {
    if (props.submission_id) {
        
      const token = getToken();
  
      if (!token) {
        throw new Error('Failed to get access token');
      }
      getAPI(`/staff/answers/${props.submission_id}?token=${token}`)
        .then((res) => {
          if (res.code === 200) {
            setAnswerDetail(res.data);
          }
        })
        .finally(() => {});
    }
  }, [props.submission_id]);

  const validate = () => {
    let keyError = '';
    answerDetail.metadata?.fields?.map((field: any) => {
      switch (field.type) {
        case 'text':
          if (field.required && isEmpty(answerDetail.answers[field.field_id]?.value)) {
            keyError = `${field.label}を入力してください`;
          } else if (
            (field.min_length && answerDetail.answers[field.field_id]?.value && answerDetail.answers[field.field_id]?.value.length < field.min_length) ||
            (field.max_length && answerDetail.answers[field.field_id]?.value && answerDetail.answers[field.field_id]?.value.length > field.max_length)
          ) {
            keyError = `${field.label} 最小文字数 ${field.min_length}, 最大文字数 ${field.max_length}`;
          }
          break;
        case 'textarea':
          if (field.required && isEmpty(answerDetail.answers[field.field_id]?.value)) {
            keyError = `${field.label}を入力してください`;
          } else if (
            (field.min_length && answerDetail.answers[field.field_id]?.value && answerDetail.answers[field.field_id]?.value.length < field.min_length) ||
            (field.max_length && answerDetail.answers[field.field_id]?.value && answerDetail.answers[field.field_id]?.value.length > field.max_length)
          ) {
            keyError = `${field.label} 最小文字数 ${field.min_length}, 最大文字数 ${field.max_length}`;
          }
          break;
        case 'checkbox':
          if (!Array.isArray(answerDetail.answers[field.field_id]?.value)) {
            keyError = `${field.label}を入力してください`;
          } else if (
            field.required &&
            answerDetail.answers[field.field_id]?.value.length === 0
          ) {
            keyError = `${field.label}を入力してください`;
          } else if (
            (field.min_selection && answerDetail.answers[field.field_id]?.value.length < field.min_selection) ||
            (field.max_selection && answerDetail.answers[field.field_id]?.value.length > field.max_selection)
          ) {
            keyError = `${field.label} 最小 ${field.min_selection}, 最大 ${field.max_selection}`;
          }
          break;
        case 'radio':
          if (field.required && isEmpty(answerDetail.answers[field.field_id]?.value)) {
            keyError = `${field.label}を入力してください`;
          }
          break;
        case 'date':
          if (field.required && isEmpty(answerDetail.answers[field.field_id]?.value)) {
            keyError = `${field.label}を入力してください`;
          } else if (
            dayjs(answerDetail.answers[field.field_id]?.value)
              ?.format('YYYY/MM/DD')
              .toString() === 'Invalid Date'
          ) {
            keyError = `${field.label}を入力してください`;
          } else if (
            field.min_date &&
            dayjs(field.min_date) > dayjs(answerDetail.answers[field.field_id]?.value)
          ) {
            keyError = `${field.label} 最小日付 ${field.min_date}`;
          } else if (
            field.max_date &&
            dayjs(field.max_date) < dayjs(answerDetail.answers[field.field_id]?.value)
          ) {
            keyError = `${field.label} 最大日付 ${field.max_date}`;
          }
          break;
        case 'time':
          if (field.required && isEmpty(answerDetail.answers[field.field_id]?.value)) {
            keyError = `${field.label}を入力してください`;
          } else if (
            dayjs(
              `${dayjs().format('YYYY-MM-DD')} ${
                answerDetail.answers[field.field_id]?.value
              }`
            )
              ?.format('YYYY/MM/DD')
              .toString() === 'Invalid Date'
          ) {
            keyError = `${field.label}を入力してください`;
          } else if (
            field.min_time &&
            dayjs(`${dayjs().format('YYYY-MM-DD')} ${field.min_time}`) >
              dayjs(
                `${dayjs().format('YYYY-MM-DD')} ${
                  answerDetail.answers[field.field_id]?.value
                }`
              )
          ) {
            keyError = `${field.label} 最小時間 ${field.min_time}`;
          } else if (
            field.max_time &&
            dayjs(`${dayjs().format('YYYY-MM-DD')} ${field.max_time}`) <
              dayjs(
                `${dayjs().format('YYYY-MM-DD')} ${
                  answerDetail.answers[field.field_id]?.value
                }`
              )
          ) {
            keyError = `${field.label} 最大時間 ${field.max_time}`;
          }
          break;
        default:
          break;
      }
      return field;
    });
    if (keyError) {
      notifyContext.setNotifyMessage({
        show: true,
        message: `${keyError}`,
        type: 'error',
      });
      return false;
    }

    notifyContext.setNotifyMessage({
      show: false,
      message: '',
      type: 'error',
    });

    return true;
  };

  const handleSubmit = () => {
    try {
      if (!validate()) {
        return;
      }
        
      const token = getToken();
  
      if (!token) {
        throw new Error('Failed to get access token');
      }

      postAPI(`/staff/answers?token=${token}`, { ...answerDetail, user_id: 1 })
        .then((res: any) => {
          if (res.code === 200) {
            console.log(res);
            notifyContext.setNotifyMessage({
              show: true,
              message: '保存出来ました。',
              type: 'success',
            });
            props.handleConfirm();
          } else {
            notifyContext.setNotifyMessage({
              show: true,
              message: res.msg || 'Failed to post data',
              type: 'error',
            });
          }
        })
        .finally(() => {});
    } catch (error) {
      console.error('Failed to fetch data:', error);
      throw error;
    }
  };

  const handleChangeValue = (name: string, value: any) => {
    const temp = { ...answerDetail.answers };
    temp[name] = value;
    setAnswerDetail((prev: any) => ({
      ...prev,
      answers: { ...temp },
    }));
  };

  return (
    <Dialog
      open={props.open}
      onClose={props.handleClose}
      aria-labelledby='alert-dialog-title'
      aria-describedby='alert-dialog-description'
    >
      <DialogTitle id='scroll-dialog-title'>
        <Stack
          direction={'row'}
          justifyContent={'space-between'}
          alignItems={'center'}
        >
          <Typography fontSize={'20px'} fontWeight={500}>
            問診表編集
          </Typography>
          <IconButton
            aria-label='toggle password visibility'
            edge='end'
            onClick={props.handleClose}
          >
            <Box
              component={'img'}
              src='/images/ic-close.svg'
              alt='icon eye'
              sx={{ width: '24px', height: '24px' }}
            />
          </IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent sx={{ width: '500px' }}>
        {Array.isArray(answerDetail?.metadata?.fields) &&
          sortObject(answerDetail?.metadata.fields).map(
            (field: any, index: number) => {
              return (
                <GenerateField
                  field={field}
                  key={index}
                  answer={answerDetail?.answers ? answerDetail?.answers[field.field_id] : null}
                  handleChangeValue={handleChangeValue}
                />
              );
            }
          )}
        <Stack alignItems={'center'}>
          <Typography></Typography>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ p: '20px' }}>
        <BtnOutlineGradient
          text='キャンセル'
          handleButtonClick={props.handleClose}
        />
        <BtnGradient text='登録' handleButtonClick={handleSubmit} />
      </DialogActions>
    </Dialog>
  );
};

export default DialogForm;
